import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { styled } from "@mui/system";
import {
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  IconButton,
  Slider,
  Snackbar,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { updateJobAPI } from "../../../services/jobService"; // Assuming an API service for updating jobs

const FormContainer = styled(Paper)({
  padding: "50px",
  marginBottom: "20px",
  borderRadius: "14px",
});

const FormTitle = styled(Typography)({
  fontFamily: "Raleway",
  fontSize: "21px",
  color: "#304C68",
  fontWeight: 600,
  lineHeight: "24.65px",
  marginBottom: "20px",
});

const FormGroup = styled(Box)({
  marginBottom: "20px",
});

const FormLabel = styled(Typography)({
  fontFamily: "Raleway",
  fontWeight: 500,
  fontSize: "14px",
  color: "#000",
  lineHeight: "22.4px",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginTop: "20px",
});

const CreateTabButton = styled(Button)({
  width: "294px",
  height: "56px",
  fontFamily: "Raleway",
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "18.78px",
  borderRadius: "8px",
  padding: "8px",
});

const EditJobForm = ({ job, onSave, onCancel }) => {
  const dispatch = useDispatch(); // Initialize dispatch hook for Redux
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    company: "",
    location: "",
    // department: "",
    // type: "",
    // startDate: null,
    weighting_parameters: {
      relevance: 0.2,
      skills: 0.2,
      education: 0.2,
      work_experience: 0.2,
      achievements: 0.2,
    },
  });

  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (job) {
      setFormData({
        ...job,
        title: job['Job Title'] || '',  // Pre-fill with actual job title from the provided structure
        company: job['Company Name'] || '',  // Pre-fill with actual company name from the provided structure
        // added location change
        location: job['Location'] || ' ',
        startDate: job.startDate ? dayjs(job.startDate) : null,
        weighting_parameters: job.weighting_parameters || {
          relevance: 0.2,
          skills: 0.2,
          education: 0.2,
          work_experience: 0.2,
          achievements: 0.2,
        },
      });
    }
  }, [job]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };
  

  const handleSliderChange = (name) => (event, value) => {
    setFormData((prevData) => ({
      ...prevData,
      weighting_parameters: {
        ...prevData.weighting_parameters,
        [name]: value,
      },
    }));
  };

  const handleStartDatePickerChange = (date) => {
    setFormData({
      ...formData,
      startDate: date,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSave = async () => {
    let errorpresent = false;
  
    const weightingParams = formData.weighting_parameters;
  
    // Calculate the sum of the user-selected weighting parameters
    const sum =
      weightingParams.relevance +
      weightingParams.skills +
      weightingParams.education +
      weightingParams.work_experience +
      weightingParams.achievements;
  
    // Normalize the values
    const normalizedParams = {
      relevance: weightingParams.relevance / sum,
      skills: weightingParams.skills / sum,
      education: weightingParams.education / sum,
      work_experience: weightingParams.work_experience / sum,
      achievements: weightingParams.achievements / sum,
    };
  
    const newErrors = {};
    const mandatoryFields = [
      // "title",
      // "description",
      // "company",
      // "location",
      // "department",
      // "type",
    ];
  
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else if (!errorpresent) {
      const jobData = {
        // ...formData,
        description: formData.description,
        weighting_parameters: normalizedParams, // Send normalized parameters
        job_title : formData.title , 
        company_name : formData.company,
        location : formData.location ,
      };
  
      try {
        await updateJobAPI(job["Job Id"], jobData);
  
        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage: "Job Updated Successfully",
          })
        );
  
        onSave(formData);
      } catch (error) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: error.message,
          })
        );
      }
    }
  };
  
  return (
    <FormContainer>
      <FormTitle>Edit Job</FormTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Title*</FormLabel>
            <TextField
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter Title"
              variant="outlined"
              fullWidth
              error={!!errors.title}
              helperText={errors.title}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Company*</FormLabel>
            <TextField
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              placeholder="Enter Company Name"
              variant="outlined"
              fullWidth
              required
              error={!!errors.company}
              helperText={errors.company}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <FormGroup>
            <FormLabel variant="subtitle1">Location*</FormLabel>
            <TextField
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              required
              error={!!errors.location}
              placeholder="Enter Location"
              sx={{
                "& .MuiInputBase-input": {
                  color: formData.location === "" ? "#B0B0B0" : "inherit", // Grey color when placeholder is visible
                },
              }}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <FormGroup>
            <FormLabel variant="subtitle1">Job Description*</FormLabel>
            <TextField
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter Job Description"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              required
              error={!!errors.description}
              helperText={errors.description}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <FormTitle>Weighting Parameters</FormTitle>
        </Grid>

        {["relevance", "skills", "education", "work_experience", "achievements"].map(
          (param) => (
            <Grid item xs={12} md={6} key={param}>
              <FormGroup>
                <FormLabel variant="subtitle1">{param}</FormLabel>
                <Slider
                  name={param}
                  value={formData.weighting_parameters[param]}
                  min={0}
                  max={1}
                  step={0.1}
                  marks={[
                    { value: 0, label: "0" },
                    { value: 0.1, label: "0.1" },
                    { value: 0.2, label: "0.2" },
                    { value: 0.3, label: "0.3" },
                    { value: 0.4, label: "0.4" },
                    { value: 0.5, label: "0.5" },
                    { value: 0.6, label: "0.6" },
                    { value: 0.7, label: "0.7" },
                    { value: 0.8, label: "0.8" },
                    { value: 0.9, label: "0.9" },
                    { value: 1, label: "1" },
                  ]}
                  onChange={handleSliderChange(param)}
                />
              </FormGroup>
            </Grid>
          )
        )}

        <Grid item xs={12}>
          <ButtonContainer>
            <CreateTabButton
              onClick={handleSave}
              sx={{
                mr: 2,
                backgroundColor: "#5088FF",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#4C679D",
                },
              }}
            >
              Save
            </CreateTabButton>

            <CreateTabButton
              onClick={onCancel}
              sx={{
                backgroundColor: "#F6F7F8",
                color: "#274456",
                "&:hover": {
                  backgroundColor: "#F6F7F8",
                },
              }}
            >
              Cancel
            </CreateTabButton>
          </ButtonContainer>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Job updated successfully"
      />
    </FormContainer>
  );
};

export default EditJobForm;
